'use client'

import React, { useState, useEffect, lazy, Suspense } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { TerpeneList } from '../charts/TerpeneList'

// ✅ OPTIMIZED: Lazy load Recharts to reduce initial bundle by ~44KB
const TerpeneProfileChart = lazy(() => import('../charts/TerpeneProfileChart').then(mod => ({ default: mod.TerpeneProfileChart })))

// Animation variants for slide transitions
const slideVariants = {
  enter: {
    opacity: 0,
    x: 100,
  },
  center: {
    opacity: 1,
    x: 0,
  },
  exit: {
    opacity: 0,
    x: -100,
  },
}

const slideTransition = {
  duration: 0.5,
  ease: [0.4, 0, 0.2, 1] as const,
}

// Interface for slide content props
interface SlideProps {
  onOpenSampleModal?: () => void
  onOpenContactModal?: () => void
  onOpenTerpeneModal?: () => void
}

// Slide 2: "Live Resin is the Gold Standard"
export function GoldStandardSlide() {
  const features = [
    {
      title: "Superior Taste",
      description: "Full terpene profile preserved from harvest",
      tone: 'from-eskerium-blue/15 to-blue-500/10 border-blue-400/20'
    },
    {
      title: "Premium Quality",
      description: "Flash-frozen at peak freshness",
      tone: 'from-eskerium-gold/15 to-yellow-500/10 border-yellow-400/20'
    },
    {
      title: "Maximum Effectiveness",
      description: "Higher bioavailability through entourage effect",
      tone: 'from-emerald-500/15 to-emerald-400/10 border-emerald-400/20'
    },
    {
      title: "Health Benefits",
      description: "No degradation, oxidation, or contamination",
      tone: 'from-purple-500/15 to-indigo-500/10 border-purple-400/20'
    }
  ]

  return (
    <motion.div
      variants={slideVariants}
      initial="enter"
      animate="center"
      exit="exit"
      transition={slideTransition}
      className="h-full flex flex-col space-y-6 p-4"
    >
      {/* Heading */}
      <div className="text-center space-y-2">
        <h2 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-eskerium-gold via-yellow-300 to-eskerium-gold bg-clip-text text-transparent">
          Live Resin is the Gold Standard
        </h2>
        <p className="text-white/70 text-sm md:text-base">
          Why Industry Leaders Choose Live Resin
        </p>
      </div>

      {/* Features Grid (compact spacing on desktop) */}
      <div className="grid grid-cols-2 gap-3 md:gap-3 lg:gap-4 flex-1">
        {features.map((feature, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05 + 0.2 }}
            className={`relative card-glass gradient-border edge-internal-glow edge-internal-glow-soft p-3 md:p-3 lg:p-4 hover:shadow-elev-2 transition-all duration-300 ${feature.tone}`}
          >
            <h3 className="text-white font-medium text-[13px] md:text-sm lg:text-base mb-0.5 lg:mb-1">
              {feature.title}
            </h3>
            <p className="text-white/60 text-[11px] md:text-xs lg:text-sm leading-relaxed">
              {feature.description}
            </p>
          </motion.div>
        ))}
      </div>

      {/* Bottom Row: 2/3 comparison + 1/3 Learn More */}
      <div className="grid grid-cols-3 gap-3 md:gap-3 lg:gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="col-span-3 md:col-span-2 bg-gradient-to-br from-white/5 to-white/10 backdrop-blur-sm rounded-lg p-4 md:p-3 lg:p-4 border border-white/10"
        >
          <h3 className="text-white font-medium mb-2 md:mb-2.5 text-center text-sm md:text-[15px] lg:text-base">
            Think Farm-to-Table vs Packaged Foods
          </h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="text-center">
              <div className="text-green-400 font-medium mb-1">Live Resin</div>
              <div className="text-white/60 text-xs space-y-1">
                <div>• Fresh</div>
                <div>• Full spectrum</div>
                <div>• Natural</div>
              </div>
            </div>
            <div className="text-center">
              <div className="text-red-400 font-medium mb-1">Distillate</div>
              <div className="text-white/60 text-xs space-y-1">
                <div>• Processed</div>
                <div>• Isolated</div>
                <div>• Artificial</div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Learn More 1/3 card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="col-span-3 md:col-span-1 flex flex-col justify-between bg-gradient-to-br from-eskerium-gold/10 to-eskerium-blue/10 backdrop-blur-sm rounded-lg p-4 md:p-3 lg:p-4 border border-white/10"
        >
          <div>
            <p className="text-white/60 text-xs md:text-[13px] lg:text-sm">Learn more about how we produce superior ingredients</p>
          </div>
          <Button
            size="sm"
            variant="outline"
            className="mt-3 border-white/20 text-white hover:bg-white/10"
            onClick={() => window.dispatchEvent(new CustomEvent('open-how-we-elevate-hemp'))}
          >
            More
          </Button>
        </motion.div>
      </div>
    </motion.div>
  )
}


// Slide 1: Lab Results - Terpene Profile
export function LabResultsSlide({ onOpenTerpeneModal }: SlideProps) {
  return (
    <motion.div
      variants={slideVariants}
      initial="enter"
      animate="center"
      exit="exit"
      transition={slideTransition}
      className="h-full flex flex-col space-y-4 p-4"
    >
      {/* Terpene Profile Chart - Lazy loaded with Suspense */}
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.3 }}
        className="bg-white/5 backdrop-blur-sm rounded-lg p-4 border border-white/10"
      >
        <Suspense fallback={<Skeleton className="w-full h-[220px]" />}>
          <TerpeneProfileChart />
        </Suspense>
      </motion.div>

      {/* Terpene List Card with Info Button */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="bg-white/5 backdrop-blur-sm rounded-lg p-4 border border-white/10"
      >
        {/* Header with Info Button */}
        <div className="flex justify-between items-start mb-3">
          <h4 className="text-white/70 text-sm font-mono">Terpene Breakdown</h4>
          <Button
            onClick={onOpenTerpeneModal}
            variant="outline"
            size="sm"
            className="border-yellow-400 text-yellow-300 hover:bg-yellow-500/10 hover:border-yellow-300"
          >
            Terpene Info
          </Button>
        </div>
        
        {/* Terpene List Component */}
        <TerpeneList />
      </motion.div>
    </motion.div>
  )
}